import React, { useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { MultiPolygon } from 'geojson';
import type { Boundary } from '@tcc/shared-data-lib';

import type { FormData } from 'create/bulk/common/types';
import { useAppSelector } from 'app/hooks';
import { drawingStates } from 'constants/values';
import { getGoogleCredentials } from 'util/credentials';
import { getMessage } from 'util/locale';
import { getAccountCountry } from 'user/selectors';
import { isEu9Limited } from 'constants/countryCodes';

import BaseMap from 'mapping/BaseMap';
import FieldOutlineLayer from 'mapping/layers/FieldOutlineLayer';
import EditingLayer from 'mapping/layers/EditingLayer';
import BulkReviewLayer from './layers/BulkReviewLayer';
import { PrescriptionZone } from './types';

interface ReviewScriptMapProps {
  fieldUuid: string;
  onClickZone: (zoneId: string | null) => void;
  seedingRateBins: [number, number][];
  selectedZoneId: string | null;
  zones: PrescriptionZone[];
}

export interface ParsedZone extends PrescriptionZone {
    geoJson: {
      geometry: {
        type: Boundary;
        coordinates: number[][][][];
      };
      properties: Record<string, unknown>;
      type: 'Feature';
    };
    meta: {
      zoneId: string;
    };
  }

/** Component for rendering map on Review Prescriptions screen (bulk create advanced script flow).  */
const baseString = 'bulk-review-script-map';
const ReviewScriptMap = ({
  fieldUuid,
  onClickZone,
  seedingRateBins,
  selectedZoneId,
  zones
}: ReviewScriptMapProps) => {
  const accountCountry = useAppSelector(getAccountCountry);

  const { control } = useFormContext<FormData>();
  const fields = useWatch({
    control,
    name: 'fields',
  });

  const onClickOutsideZones = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const _isZoneLayer = (el: EventTarget | null) => (el as HTMLElement).nodeName.toLowerCase() === 'path';

    if (!_isZoneLayer(event.target)) {
      onClickZone(null);
    }
  }, [onClickZone]);

  const parsedBoundaries = useMemo(() => {
    const boundaries = fields.find((field) => field.id === fieldUuid).boundary;

    return {
      type: boundaries.type,
      coordinates: Object.values(boundaries).find((values) => Array.isArray(values)),
    };
  }, [fields, fieldUuid]);

  const parsedZones = useMemo(() => zones.reduce((zonesWithExtraData, zone) => {
    zonesWithExtraData[zone.id] = {
      ...zone,
      geoJson: {
        type: 'Feature' as const,
        geometry: {
          type: zone.geometry.type as unknown as Boundary,
          coordinates: (zone.geometry as MultiPolygon).coordinates,
        },
        properties: {},
      },
      meta: {
        zoneId: zone.id,
      },
    };

    return zonesWithExtraData;
  }, {} as Record<string, ParsedZone>), [zones]);

  const selectedZoneIds = useMemo(() => selectedZoneId ? [selectedZoneId] : [], [selectedZoneId]);

  const disclaimer = useMemo(() => isEu9Limited(accountCountry)
    ? <span className={'vra-emea-disclaimer'}>{getMessage('disclaimer--emea-seed-density')}</span>
    : null, [accountCountry]);

  return (
    <div className={baseString} data-test-id={baseString}>
      <BaseMap
        boundaries={parsedBoundaries}
        boundariesPadding={0.005} // % padding around the field
        className='vra-map'
        googleCredentials={getGoogleCredentials()}
        onClick={onClickOutsideZones}
        onLeafletZoomEnd={() => undefined}
        showScale={false}>
        {parsedBoundaries && <FieldOutlineLayer boundaryGeoJson={parsedBoundaries} /> }
        {zones
          && <BulkReviewLayer
            zones={parsedZones}
            selectedZoneIds={selectedZoneIds}
            seedingRateBins={seedingRateBins}
          />}
        {zones
          && <EditingLayer
            drawingState={drawingStates.NONE}
            onClickZone={onClickZone}
            selectedZoneIds={selectedZoneIds}
            zones={parsedZones}
          />
        }
      </BaseMap>
      {disclaimer}
    </div>
  );
};

export default ReviewScriptMap;
